/**
 * branchAssumption.js — Pure string builders for branch assumption text.
 *
 * These builders produce the server-facing assumption strings that are hand-synced
 * between branchTrigger.js (triggerToAssumption) and reforecast's inline branchAssumption.
 * Both sites import from here so the phrasing lives in one place; each site keeps its
 * own gating logic (triggerToAssumption's >=0.9 split; reforecast's null-for-mid-range).
 *
 * PHRASE CONTRACT: the forecast server pattern-matches these exact strings — both
 * `sniff_branch_sense` (branch-sense fallback) and `branch_display_title` (the
 * reader-facing "If A, will B?" title rebuild) in
 * experimental/sooth-agents/src/observatory/panel_forecast/trigger_gen.py.
 * Never change the phrasing on one side only.
 *
 * All functions are pure: no side effects, no imports, no DOM or React dependencies.
 */

/**
 * Scenario-effect suffix appended to occur / not-occur assumptions: it states the
 * SCENARIO's expected effect on the resolution probability ("yes" → expected to
 * increase, "no" → decrease). branchDirContext feeds it the NET scenario direction,
 * so the subject is the scenario — not the trigger, which is why it no longer says
 * "this event was assessed…" (that misdescribed the trigger on inverted not-occur
 * branches). Leading-space string for "yes"/"no", empty otherwise.
 *
 * @param {"yes"|"no"|string|undefined} direction  net scenario direction
 * @returns {string}
 */
export function dirContextFor(direction) {
  if (direction === "yes")
    return " Under this scenario, the probability of the resolution is expected to increase.";
  if (direction === "no")
    return " Under this scenario, the probability of the resolution is expected to decrease.";
  return "";
}

/**
 * Direction clause for a BRANCH, accounting for which assumption was made.
 * The trigger's own `direction` is its effect WHEN IT OCCURS. An occur branch
 * keeps that; an ABSENT branch (does-not-occur / removed) INVERTS it — the event
 * failing to happen pushes the forecast the opposite way from it happening (its
 * not-occur outcome sits on the opposite side of the headline probability from its
 * occur outcome). One place owns the flip so the two hand-synced call sites
 * (branchTrigger / reforecast) can't drift.
 *
 * @param {"yes"|"no"|string|undefined} direction  the trigger's occur-direction
 * @param {{ absent?: boolean }} [opts]  absent=true for does-not-occur / removed
 * @returns {string}
 */
export function branchDirContext(direction, { absent = false } = {}) {
  const net = !absent
    ? direction
    : direction === "yes"
      ? "no"
      : direction === "no"
        ? "yes"
        : direction;
  return dirContextFor(net);
}

/**
 * Assumption string for "event has occurred" branches.
 *
 * @param {{ title: string, date: string, dirContext: string }} params
 * @returns {string}
 */
export function occurAssumption({ title, date, dirContext }) {
  return `Event "${title}" (${date}) has occurred.${dirContext} Adjust the forecast probability accordingly.`;
}

/**
 * Assumption string for "event does NOT occur" branches.
 *
 * @param {{ title: string, date: string, dirContext: string }} params
 * @returns {string}
 */
export function notOccurAssumption({ title, date, dirContext }) {
  return `Event "${title}" (${date}) does NOT occur.${dirContext} Adjust the forecast probability accordingly.`;
}

/**
 * Assumption string for custom user-supplied events.
 *
 * @param {{ date: string, title: string }} params
 * @returns {string}
 */
export function customEventAssumption({ date, title }) {
  return `Assume the following: On ${date}, this event occurs: "${title}". Factor this into the forecast and adjust the probability accordingly.`;
}
